// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { matchPath } from 'react-router';

export interface Sticker {
    id: string;
    location?: string;
    paths: string[];
    image: () => string;
    order: number;
    entryPoint?: boolean;
}

const icon = (id: string) => () => require(`images/sticker-collection/sticker/${id}.png`);

export const getStickers = (isPowerUserPanel: boolean, userType: string): Sticker[] => {
    const stickers = [
        {
            id: 'joystick',
            order: 8,
            paths: ['/smb/web/view/:id/connection-info'],
            image: icon('joystick'),
        },
        {
            id: 'elvis',
            order: 1,
            paths: isPowerUserPanel ? ['/smb/web/view', '/smb/web/overview', '/smb/web/setup'] : ['/cp/home', '/admin/home'],
            image: icon('elvis'),
            entryPoint: true,
        },
        {
            id: 'skateboard',
            order: 12,
            paths: ['/smb/mail-settings/list'],
            image: icon('skateboard'),
        },
        {
            id: 'yuppie',
            order: 25,
            paths: ['/smb/database/edit-user'],
            image: icon('yuppie'),
        },
        {
            id: 'floppy',
            order: 24,
            paths: ['/smb/statistics/details'],
            image: icon('floppy'),
        },
        {
            id: 'hustler',
            order: 17,
            paths: ['/smb/user/overview'],
            image: icon('hustler'),
        },
        {
            id: 'telephone',
            order: 20,
            paths: ['/smb/ftp/create-user'],
            image: icon('telephone'),
        },
        {
            id: 'boombox',
            order: 2,
            paths: ['/smb/web/php-settings'],
            image: icon('boombox'),
        },
        {
            id: 'delorean',
            order: 18,
            paths: ['/smb/ssl-certificate/list'],
            image: icon('delorean'),
        },
        {
            id: 'pizza',
            order: 16,
            paths: ['/smb/log-file/list'],
            image: icon('pizza'),
        },
        {
            id: 'synthesizer',
            order: 15,
            paths: ['/smb/dns-zone/records-list'],
            image: icon('synthesizer'),
        },
        {
            id: 'rollers',
            order: 3,
            paths: ['/smb/file-manager/list'],
            image: icon('rollers'),
        },
        {
            id: 'ghost',
            order: 19,
            paths: ['/smb/scheduler/settings'],
            image: icon('ghost'),
        },
    ];

    const toolsStickers = [
        {
            id: 'electroguitar',
            order: 7,
            paths: ['/admin/server/tools', '/admin/reseller/tools'],
            image: icon('electroguitar'),
        },
        {
            id: 'arcade-cabinet',
            order: 6,
            paths: ['/cp/server/virtual-host-template/', '/cp/reseller-tools/virtual-host-template'],
            image: icon('arcade-cabinet'),
        },
        {
            id: 'cassette',
            order: 4,
            paths: ['/admin/backup/list/'],
            image: icon('cassette'),
        },
        {
            id: 'aerobics-girl',
            order: 9,
            paths: ['/cp/settings-ui/edit-interface-view', '/admin/reseller/preferences'],
            image: icon('aerobics-girl'),
        },
        isPowerUserPanel ? undefined : {
            id: 'player',
            order: 22,
            paths: ['/admin/subscription/change-current'],
            image: icon('player'),
        },
        isPowerUserPanel ? undefined : {
            id: 'officer',
            order: 21,
            paths: ['/admin/customer-service-plan/create-addon'],
            image: icon('officer'),
        },
        {
            id: 'tv',
            order: 14,
            paths: ['/cp/server/info-and-statistics/summary-report', '/cp/reseller-tools/info-and-statistics/summary-report'],
            image: icon('tv'),
        },
    ];

    const adminStickers = [
        {
            id: 'pc',
            order: 10,
            paths: ['/admin/services/list'],
            image: icon('pc'),
        },
        {
            id: 'alien',
            order: 13,
            paths: ['/cp/server/mail/settings'],
            image: icon('alien'),
        },
        {
            id: 'aviator-glasses',
            order: 5,
            paths: ['/admin/php-handler/list'],
            image: icon('aviator-glasses'),
        },
        {
            id: 'zombie',
            order: 11,
            paths: ['/admin/sessions/settings/'],
            image: icon('zombie'),
        },
        {
            id: 'sneakers',
            order: 23,
            paths: ['/modules/catalog/'],
            image: icon('sneakers'),
        },
    ];

    return [
        ...stickers,
        ...(['ADMIN', 'RESELLER'].includes(userType) ? toolsStickers : []),
        ...('ADMIN' === userType ? adminStickers : []),
    ].filter((sticker): sticker is Sticker => sticker !== undefined);
};

const matchesPath = (sticker: Sticker, pathname: string): boolean => sticker.paths.some(path => path !== '' && matchPath({ path, end: false }, pathname) !== null);

export const findSticker = (stickers: Sticker[], pathname: string): Sticker | undefined => stickers.find(sticker => matchesPath(sticker, pathname));
